/*eslint-disable*/
import React, { useMemo, useState, useEffect } from "react";
import DisplayReport from "./LayoutComponents/DisplayReport";

import FastAPIClient from "../../src/client";
import config from "../../src/config";

import { MeetingsProps, Visit } from "../types/meetings.t";

const client = new FastAPIClient(config);

const formatDate = (timestamp: string): string => {
    const date = new Date(timestamp);

    // Get day, month, and year
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "long" }); // Full month name
    const year = date.getFullYear();

    // Get hours and minutes
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day} ${month} ${year} à ${hours}:${minutes}`;
};

const Meetings: React.FC<MeetingsProps> = ({ visithistory, openModal }) => {
    const today = new Date();
    today.setHours(0, 1, 0, 0); // Set the time to 23:59 to compare only dates

    const [reportLayout, setReportLayout] = useState([]);

    const language = "fr";

    const getReportLayout = () => {
        client.getReportLayout().then((data) => {
            setReportLayout(data);
        });
    };

    useEffect(() => {
        getReportLayout();
    }, []);

    const [isExpanded, setIsExpanded] = useState(false);

    // Use useMemo to filter and sort past and upcoming meetings
    const { pastMeetings, upcomingMeetings } = useMemo(() => {
        if (!visithistory) return { pastMeetings: [], upcomingMeetings: [] };

        const pastMeetings = visithistory
            .filter((meeting) => {
                const meetingDate = new Date(meeting.start_date);
                return meetingDate != today && meeting.is_visited === true;
            })
            .sort(
                (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime()
            );

        const upcomingMeetings = visithistory
            .filter((meeting) => {
                const meetingDate = new Date(meeting.start_date);
                return meetingDate >= today && meeting.is_visited === false;
            })
            .sort(
                (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime()
            );

        return { pastMeetings, upcomingMeetings };
    }, [visithistory]);

    // Group past meetings by month
    const groupPastMeetingsByMonth = (meetings: Visit[]) => {
        return meetings.reduce(
            (groups, meeting) => {
                const month = new Date(meeting.start_date).toLocaleString(
                    "default",
                    { month: "long", year: "numeric" }
                );
                if (!groups[month]) {
                    groups[month] = [];
                }
                groups[month].push(meeting);
                return groups;
            },
            {} as { [key: string]: Visit[] }
        );
    };

    const groupedPastMeetings = groupPastMeetingsByMonth(pastMeetings);

    // Check if there are no meetings at all
    const noMeetings =
        upcomingMeetings.length === 0 && pastMeetings.length === 0;

    const noUpcomingMeetings = upcomingMeetings.length === 0;

    return (
        <div className="pt-10 w-full">
            {noMeetings && (
                <div
                    className="rounded-xl text-left p-4 text-white flex flex-row items-center"
                    style={{
                        background:
                            "linear-gradient(to right, #ff6ec7, #d800ff, #ff7f00)",
                        WebkitBackgroundClip: "border-box", // Ensures the gradient is applied to the border
                    }}
                >
                    <div className="flex flex-col w-full md:text-base text-sm">
                        <strong>
                            Il est temps de planifier votre première réunion !
                        </strong>{" "}
                        Chaque bonne relation commence par un premier échange.
                    </div>
                    <button
                        onClick={openModal}
                        className="flex text-sm text-black bg-white rounded-full font-bold px-3 py-1"
                    >
                        Commencer
                    </button>
                </div>
            )}

            {!noMeetings && !noUpcomingMeetings && (
                <div className="flex w-full justify-end">
                    <button
                        onClick={openModal}
                        className="font-sm text-black bg-gold rounded-full font-bold py-1 px-2"
                    >
                        + Réunion
                    </button>
                </div>
            )}

            {/* Upcoming Meetings Section */}
            {!noMeetings && (
                <div>
                    {upcomingMeetings.length > 0 && (
                        <>
                            <h2 className="text-base mb-12 text-left">
                                À venir
                            </h2>
                            {upcomingMeetings.map((meeting, index) => (
                                <div
                                    key={meeting.meeting_id || index}
                                    className="list-wrapper mb-12 text-xs rounded-lg border border-gray-200 p-4"
                                >
                                    <div className="flex justify-between items-center">
                                        {/* Left side: Meeting title, first name, and last name */}
                                        <div className="flex flex-row w-3/5 text-left">
                                            <div className="text-base font-semibold w-2/3">
                                                {meeting.title}
                                            </div>
                                            <div className="text-sm w-1/3">
                                                {meeting.user?.first_name}{" "}
                                                {meeting.user?.last_name}
                                            </div>
                                        </div>

                                        {/* Right side: Date */}
                                        <div className="text-sm w-2/5 text-right">
                                            {formatDate(meeting.start_date)}
                                        </div>
                                    </div>
                                </div>
                            ))}{" "}
                        </>
                    )}
                    {upcomingMeetings.length === 0 && (
                        <div
                            className="rounded-xl text-left p-4 text-white flex flex-row mb-5"
                            style={{
                                background:
                                    "linear-gradient(to right, #ff6ec7, #d800ff, #ff7f00)",
                                WebkitBackgroundClip: "border-box", // Ensures the gradient is applied to the border
                                // border: "2px solid transparent", // Makes the border transparent so the gradient shows
                                // borderRadius: "1rem", // Applies rounded corners
                            }}
                        >
                            <div className="flex flex-col w-full md:text-base text-sm">
                                <strong>
                                    Il est temps de planifier votre prochaine
                                    réunion !
                                </strong>{" "}
                            </div>{" "}
                            <button
                                onClick={openModal}
                                className="flex text-sm text-black bg-white rounded-full font-bold px-3 py-1"
                            >
                                Commencer
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Past Meetings Section Grouped by Month */}
            {!noMeetings && (
                <div>
                    {Object.keys(groupedPastMeetings).map((month, index) => (
                        <div key={month}>
                            <h2 className="text-base mb-12 text-left">
                                {month}
                            </h2>
                            {groupedPastMeetings[month].map(
                                (meeting, index) => (
                                    <div
                                        key={meeting.meeting_id || index}
                                        className="list-wrapper mb-12 text-xs rounded-lg border border-gray-200 p-4"
                                    >
                                        <div className="flex justify-between items-center">
                                            {/* Left side: Meeting title, first name, and last name */}
                                            <div className="flex flex-row w-3/5 text-left">
                                                <div className="text-base font-semibold w-2/3">
                                                    {meeting.title}
                                                </div>
                                                <div className="text-sm w-1/3">
                                                    {meeting.user?.first_name}{" "}
                                                    {meeting.user?.last_name}
                                                </div>
                                            </div>

                                            {/* Right side: Date */}
                                            <div className="text-sm w-2/5 text-right">
                                                {formatDate(meeting.start_date)}
                                            </div>
                                        </div>

                                        <div className="items-start border-t border-gray-200 pt-2">
                                            <button
                                                onClick={() =>
                                                    setIsExpanded(!isExpanded)
                                                }
                                                className="text-blue-500 text-sm underline focus:outline-none text-left w-full mb-2"
                                            >
                                                {isExpanded
                                                    ? "Réduire"
                                                    : "Afficher le compte rendu"}
                                            </button>
                                            {isExpanded && (
                                                <DisplayReport
                                                    reportLayout={reportLayout}
                                                    report={meeting.report}
                                                    language={language}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Meetings;
